import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
  SelectOption,
  StenaSelectComponent,
} from '@app-monorepo/shared/ui/stena-components';
import { Month, PeriodMode } from '../../../models/period.model';
import { ReportingCostCenter } from '../../../models/reporting.model';

// Header row shared by /reporting, /budget, /reporting/daily, /admin/currency:
// a cost-center picker (optional), a month picker, and an optional
// actual/budget mode toggle. Emits signal-friendly change events so consumers
// can keep their existing signal-based state.
@Component({
  standalone: true,
  selector: 'app-context-picker',
  templateUrl: './context-picker.component.html',
  styleUrl: './context-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, StenaSelectComponent],
})
export class ContextPickerComponent {
  private readonly fb = inject(FormBuilder);

  readonly costCenters = input<ReportingCostCenter[]>([]);
  readonly months = input<Month[]>([]);
  readonly showCostCenter = input(true);
  readonly showMode = input(false);
  readonly initialCostCenterId = input<number | null>(null);
  readonly initialMonthId = input<number | null>(null);
  readonly initialMode = input<PeriodMode>('actual');

  readonly costCenterIdChange = output<number | null>();
  readonly monthIdChange = output<number | null>();
  readonly modeChange = output<PeriodMode>();

  readonly form = this.fb.group({
    costCenterId: this.fb.control<number | null>(null),
    monthId: this.fb.control<number | null>(null),
    mode: this.fb.control<PeriodMode>('actual', { nonNullable: true }),
  });

  readonly modeOptions: SelectOption<PeriodMode>[] = [
    { displayName: 'Actual', value: 'actual' },
    { displayName: 'Budget', value: 'budget' },
  ];

  constructor() {
    effect(() => {
      this.form.controls.costCenterId.setValue(this.initialCostCenterId(), {
        emitEvent: false,
      });
    });
    effect(() => {
      this.form.controls.monthId.setValue(this.initialMonthId(), {
        emitEvent: false,
      });
    });

    // Auto-select current month (or fiscal year in budget mode) once the list
    // loads, but only if the parent hasn't already provided a selection.
    effect(() => {
      const months = this.months();
      const mode = this.form.controls.mode.value;
      if (months.length === 0) return;
      if (this.form.controls.monthId.value !== null) return;
      if (this.initialMonthId() !== null) return;

      const now = new Date();
      const targetId =
        mode === 'budget'
          ? now.getFullYear() * 100
          : now.getFullYear() * 100 + (now.getMonth() + 1);
      const match = months.find((m) => m.monthId === targetId);
      if (!match) return;
      this.form.controls.monthId.setValue(match.monthId);
    });
    effect(() => {
      this.form.controls.mode.setValue(this.initialMode(), {
        emitEvent: false,
      });
    });

    this.form.controls.costCenterId.valueChanges.subscribe((v) =>
      this.costCenterIdChange.emit(v)
    );
    this.form.controls.monthId.valueChanges.subscribe((v) =>
      this.monthIdChange.emit(v)
    );
    this.form.controls.mode.valueChanges.subscribe((v) =>
      this.modeChange.emit(v)
    );
  }

  costCenterOptions(): SelectOption<number>[] {
    return this.costCenters().map((cc) => ({
      displayName: cc.costCenterName,
      value: cc.costCenterId,
    }));
  }

  monthOptions(): SelectOption<number>[] {
    return [...this.months()]
      .sort((a, b) => b.monthId - a.monthId)
      .map((m) => ({
        displayName: m.isFiscalYearMarker
          ? m.monthName
          : `${m.monthName} (${this.formatMonthId(m.monthId)})`,
        value: m.monthId,
      }));
  }

  private formatMonthId(monthId: number): string {
    const year = Math.trunc(monthId / 100);
    const month = monthId % 100;
    return `${year}-${String(month).padStart(2, '0')}`;
  }
}
